import LogUtil from '../../../core/utils/LogUtil';

// localStorage 키
const PHARMACY_ACCESS_TOKEN_KEY = 'pharmacy_access_token';
const PHARMACY_REFRESH_TOKEN_KEY = 'pharmacy_refresh_token';
const PHARMACY_INFO_KEY = 'pharmacy_info';

/**
 * PharmacyAuth Service
 * 약국 인증 관리 비즈니스 로직 계층
 */
export class PharmacyAuthService {
  constructor(pharmacyAuthRepository) {
    this.pharmacyAuthRepository = pharmacyAuthRepository;
    LogUtil.log('🔧 [PharmacyAuthService] 초기화 완료');
  }

  /**
   * 비밀번호 설정
   * @param {string} businessRegistrationNumber - 사업자등록번호
   * @param {string} password - 비밀번호
   * @param {string} passwordConfirm - 비밀번호 확인
   * @param {string} [currentPassword] - 현재 비밀번호 (10/22 백엔드: 이미 비밀번호가 있는 약국은 필수, 틀리면 result_code 401)
   * @returns {Promise<BaseResponse<any>>}
   */
  async setPassword(businessRegistrationNumber, password, passwordConfirm, currentPassword) {
    try {
      LogUtil.log('📤 [PharmacyAuthService] 비밀번호 설정 요청:', {
        businessRegistrationNumber,
      });

      // 데이터 검증
      if (!businessRegistrationNumber) {
        throw new Error('사업자등록번호가 필요합니다.');
      }
      if (!password) {
        throw new Error('비밀번호가 필요합니다.');
      }
      if (!passwordConfirm) {
        throw new Error('비밀번호 확인이 필요합니다.');
      }
      if (password !== passwordConfirm) {
        throw new Error('비밀번호가 일치하지 않습니다.');
      }
      if (password.length < 6) {
        throw new Error('비밀번호는 6자 이상이어야 합니다.');
      }

      const result = await this.pharmacyAuthRepository.setPassword({
        business_registration_number: businessRegistrationNumber,
        password,
        password_confirm: passwordConfirm,
        ...(currentPassword ? { current_password: currentPassword } : {}),
      });

      LogUtil.log('📥 [PharmacyAuthService] 비밀번호 설정 성공:', result);
      return result;
    } catch (error) {
      LogUtil.error('❌ [PharmacyAuthService] 비밀번호 설정 실패:', error);
      throw error;
    }
  }

  /**
   * 로그인
   * @param {string} businessRegistrationNumber - 사업자등록번호
   * @param {string} password - 비밀번호
   * @returns {Promise<BaseResponse<any>>}
   */
  async login(businessRegistrationNumber, password) {
    try {
      LogUtil.log('📤 [PharmacyAuthService] 로그인 요청:', {
        businessRegistrationNumber,
      });

      // 데이터 검증
      if (!businessRegistrationNumber) {
        throw new Error('사업자등록번호가 필요합니다.');
      }
      if (!password) {
        throw new Error('비밀번호가 필요합니다.');
      }

      const result = await this.pharmacyAuthRepository.login({
        business_registration_number: businessRegistrationNumber,
        password,
      });

      // 토큰 저장 (localStorage)
      if (result?.data?.access_token) {
        localStorage.setItem(PHARMACY_ACCESS_TOKEN_KEY, result.data.access_token);
        LogUtil.log('✅ [PharmacyAuthService] Pharmacy Access Token 저장 완료');
      }
      if (result?.data?.refresh_token) {
        localStorage.setItem(PHARMACY_REFRESH_TOKEN_KEY, result.data.refresh_token);
        LogUtil.log('✅ [PharmacyAuthService] Pharmacy Refresh Token 저장 완료');
      }
      // 약국 정보 저장
      if (result?.data?.pharmacy) {
        localStorage.setItem(PHARMACY_INFO_KEY, JSON.stringify(result.data.pharmacy));
        LogUtil.log('✅ [PharmacyAuthService] Pharmacy Info 저장 완료');
      }

      LogUtil.log('📥 [PharmacyAuthService] 로그인 성공:', result);
      return result;
    } catch (error) {
      LogUtil.error('❌ [PharmacyAuthService] 로그인 실패:', error);
      throw error;
    }
  }

  /**
   * 토큰 갱신
   * @param {string} refreshToken - 리프레시 토큰 (optional, localStorage에서 자동으로 가져옴)
   * @returns {Promise<BaseResponse<any>>}
   */
  async refreshToken(refreshToken = null) {
    try {
      LogUtil.log('📤 [PharmacyAuthService] 토큰 갱신 요청');

      // refreshToken이 없으면 localStorage에서 가져오기
      const token = refreshToken || localStorage.getItem(PHARMACY_REFRESH_TOKEN_KEY);

      if (!token) {
        throw new Error('리프레시 토큰이 없습니다.');
      }

      const result = await this.pharmacyAuthRepository.refreshToken({
        refresh_token: token,
      });

      // 새로운 토큰 저장
      if (result?.data?.access_token) {
        localStorage.setItem(PHARMACY_ACCESS_TOKEN_KEY, result.data.access_token);
        LogUtil.log('✅ [PharmacyAuthService] Pharmacy Access Token 갱신 완료');
      }
      if (result?.data?.refresh_token) {
        localStorage.setItem(PHARMACY_REFRESH_TOKEN_KEY, result.data.refresh_token);
        LogUtil.log('✅ [PharmacyAuthService] Pharmacy Refresh Token 갱신 완료');
      }

      LogUtil.log('📥 [PharmacyAuthService] 토큰 갱신 성공:', result);
      return result;
    } catch (error) {
      LogUtil.error('❌ [PharmacyAuthService] 토큰 갱신 실패:', error);
      // 갱신 실패 시 토큰 제거
      this.clearPharmacyTokens();
      throw error;
    }
  }

  /**
   * 로그아웃
   * @param {string} refreshToken - 리프레시 토큰 (optional, localStorage에서 자동으로 가져옴)
   * @returns {Promise<BaseResponse<any>>}
   */
  async logout(refreshToken = null) {
    try {
      LogUtil.log('📤 [PharmacyAuthService] 로그아웃 요청');

      // refreshToken이 없으면 localStorage에서 가져오기
      const token = refreshToken || localStorage.getItem(PHARMACY_REFRESH_TOKEN_KEY);

      if (token) {
        await this.pharmacyAuthRepository.logout({ refresh_token: token });
      }

      // 토큰 제거
      this.clearPharmacyTokens();

      LogUtil.log('📥 [PharmacyAuthService] 로그아웃 성공');
      return {
        result_code: '200',
        result_msg: '로그아웃되었습니다.',
        data: null,
      };
    } catch (error) {
      LogUtil.error('❌ [PharmacyAuthService] 로그아웃 실패:', error);
      // 실패해도 로컬 토큰은 제거
      this.clearPharmacyTokens();
      throw error;
    }
  }

  /**
   * 저장된 토큰 가져오기
   * @returns {Object} - { accessToken, refreshToken }
   */
  getPharmacyTokens() {
    return {
      accessToken: localStorage.getItem(PHARMACY_ACCESS_TOKEN_KEY),
      refreshToken: localStorage.getItem(PHARMACY_REFRESH_TOKEN_KEY),
    };
  }

  /**
   * Pharmacy Access Token 가져오기
   * @returns {string|null}
   */
  getPharmacyAccessToken() {
    return localStorage.getItem(PHARMACY_ACCESS_TOKEN_KEY);
  }

  /**
   * Pharmacy Refresh Token 가져오기
   * @returns {string|null}
   */
  getPharmacyRefreshToken() {
    return localStorage.getItem(PHARMACY_REFRESH_TOKEN_KEY);
  }

  /**
   * Pharmacy 정보 가져오기
   * @returns {Object|null}
   */
  getPharmacyInfo() {
    const info = localStorage.getItem(PHARMACY_INFO_KEY);
    if (info) {
      try {
        return JSON.parse(info);
      } catch {
        return null;
      }
    }
    return null;
  }

  /**
   * 약국 인증 상태 확인
   * @returns {boolean}
   */
  isPharmacyAuthenticated() {
    const accessToken = this.getPharmacyAccessToken();
    return !!accessToken;
  }

  /**
   * 저장된 약국 토큰 및 정보 제거
   */
  clearPharmacyTokens() {
    localStorage.removeItem(PHARMACY_ACCESS_TOKEN_KEY);
    localStorage.removeItem(PHARMACY_REFRESH_TOKEN_KEY);
    localStorage.removeItem(PHARMACY_INFO_KEY);
    LogUtil.log('🗑️ [PharmacyAuthService] 약국 토큰 및 정보 제거 완료');
  }
}
